/*
  Single source of truth for site navigation.

  NAV_SECTIONS drives both the side-nav drawer (src/SideNav.js) and the
  homepage favorites/widget catalog (components/Home/favoritesCatalog.js), so
  names, paths, and role gates are defined exactly once.

  Shape:
    section: { id, title, description, defaultOpen?, hubPath?, groups? | items? }
    group:   { title, items }        — a labeled cluster inside a section
    item:    { name, path, roles, keywords?, description?, disabled?, note? }

  Rules:
  - `roles` must let through exactly the people who can open the route
    (see Routes/RouteHandler.js). Where the route is open ("*") but the page
    only makes sense for a specific audience, the narrower audience gate is
    intentional — the route stays the authority on access, this is display.
  - `keywords` are extra words for the drawer's search box — what people
    actually type, not internal names.
  - `disabled: true` renders an unclickable "coming soon" row and keeps the
    page out of the favorites catalog.
  - `navHidden: true` keeps an item out of the drawer's browse tree but
    still searchable, favoritable, and widget-capable. Used for the
    individual submit-forms and per-form "my …" pages, which are reached
    through the Forms hub (Submit a Form / My Submissions) instead of their
    own nav rows — existing favorites and widgets keep working. New forms
    register in components/Forms/formsCatalog.js, NOT as nav items (see
    "Adding a new form" in CLAUDE.md).
  - `hubPath` makes a section a hub: the drawer shows it as ONE row that
    opens that page instead of expanding into its items, and the hub page
    lists the items as cards (with each item's `description`). The items
    stay here, so they're still searchable, favoritable, widget-capable,
    and in the Page Access catalog. Admin is the hub section today
    (views/Admin/Hub/AdminCenter.js); its first, untitled group holds the
    hub page itself.

  Organizing (keep the drawer scannable as pages are added):
  - A page that belongs to a team or product goes in that team's section,
    next to its siblings — even when only Admins can open it (Cabinet Admin
    in Cabinets, Interior Door Admin and Truth Chart in Sales & Leads).
  - Admin is only for company-wide administration and stays grouped:
    People & Access, Node Apps, Communications, Shop Floor. Add a page to
    the group it fits, with a one-line `description` for its Admin Center
    card; start a new group only for 2+ related pages. A page that brings a
    new role into Admin also adds that role to the Admin Center item.
  - Once a flat section passes ~6 visible rows, split it into groups.
    Group titles show on their own in the favorites picker, so they must
    make sense without the section name.
*/

import { isPathGranted } from "../../utils/routeAccess";

export const NAV_SECTIONS = [
  {
    id: "forms",
    title: "Forms",
    description: "Submit a form and track your submissions",
    // The hub is how everyone reaches every form — keep it open by default
    // so Order Problem, RFQs, and the rest stay two clicks away.
    defaultOpen: true,
    items: [
      {
        name: "Submit a Form",
        path: "/forms",
        roles: ["*"],
        keywords: [
          "form",
          "rfq",
          "quote",
          "order problem",
          "service request",
          "remodel",
          "recognition",
          "caught in the act",
          "feature request",
          "submit",
        ],
      },
      {
        name: "My Submissions",
        path: "/forms/my-submissions",
        roles: ["*"],
        keywords: ["status", "track", "history", "drafts", "submitted"],
      },
    ],
  },
  {
    id: "windows-doors",
    title: "Windows & Doors",
    description: "Orders, quotes, lead times, and service",
    groups: [
      {
        title: "Exterior Orders",
        items: [
          {
            name: "My Exterior Orders",
            path: "/windowdoor/exteriororders",
            roles: ["*"],
            keywords: ["delivery", "schedule", "window", "door"],
          },
          {
            name: "My Exterior Requests",
            path: "/windowdoor/requests",
            roles: ["*"],
            keywords: ["pending", "accepted", "rejected"],
          },
          {
            name: "Request Manager",
            path: "/windowdoor/admin/requestlist",
            roles: ["Window Door Manager", "Admin"],
          },
          {
            name: "Request Summary",
            path: "/windowdoor/summary",
            roles: ["Admin"],
            description: "Every exterior delivery request across the company.",
          },
        ],
      },
      {
        title: "Window Quotes & Forms",
        items: [
          {
            name: "My Window Forms",
            path: "/windowdoor/sales/windowforms",
            roles: ["Outside Sales", "Inside Sales", "Admin"],
            keywords: ["quote", "rfq"],
          },
          {
            name: "Assigned Window Forms",
            path: "/windowdoor/staff/windowforms",
            roles: ["Window Door Staff", "Window Door Manager", "Admin"],
          },
          {
            name: "Window Quote Manager",
            path: "/windowdoor/quote-manager",
            roles: ["Window Door Manager", "Admin"],
            keywords: ["assign", "quoter"],
          },
          {
            name: "Window Form Manager",
            path: "/windowdoor/quote-manager/settings",
            roles: ["Window Door Staff", "Window Door Manager", "Admin"],
            keywords: ["form builder", "notification settings"],
          },
        ],
      },
      {
        title: "Lead Times",
        items: [
          {
            name: "Lead Times",
            path: "/windowdoor/lead-times",
            roles: ["*"],
            keywords: ["brand", "window", "door", "how long"],
          },
          {
            name: "Lead Time Manager",
            path: "/windowdoor/lead-times/manage",
            roles: ["Window Door Manager", "Admin"],
          },
        ],
      },
      {
        title: "Service Requests",
        items: [
          {
            name: "New Service Request",
            path: "/windowdoor/service-request",
            roles: ["*"],
            keywords: ["repair", "broken", "warranty", "shop"],
            navHidden: true, // reached via the Forms hub
          },
          {
            name: "My Service Requests",
            path: "/windowdoor/my-service-requests",
            roles: ["*"],
            keywords: ["status", "track"],
            navHidden: true, // reached via My Submissions
          },
          {
            name: "Service Request Manager",
            path: "/windowdoor/service-requests",
            roles: ["Window Door Manager", "Admin"],
            keywords: ["technician", "queue"],
          },
        ],
      },
    ],
  },
  {
    id: "sales",
    title: "Sales & Leads",
    description: "Web leads, sales reports, and door pricing",
    items: [
      {
        name: "New RFQ",
        path: "/forms/new/rfq",
        roles: ["*"],
        keywords: ["quote", "order", "form", "interior door", "configurator"],
        navHidden: true, // reached via the Forms hub
      },
      {
        name: "My Forms",
        path: "/forms/viewall",
        roles: ["*"],
        keywords: ["drafts", "submitted"],
        navHidden: true, // reached via My Submissions
      },
      {
        name: "Remodel Form",
        path: "/forms/remodel",
        roles: ["*"],
        keywords: ["remodel", "lead", "quote", "windows", "doors"],
        navHidden: true, // reached via the Forms hub
      },
      {
        name: "Web Leads",
        path: "/webleads",
        roles: ["*"],
        keywords: ["customer", "inquiry", "assign", "lead manager"],
      },
      {
        name: "Web Lead Pin Map",
        path: "/webleads/webleadmap",
        roles: ["Web Lead Manager", "Admin", "Executive Team"],
        keywords: ["map"],
      },
      {
        name: "Truth Chart",
        path: "/truthchart",
        roles: ["Admin", "Executive Team"],
        description: "Sales rep margin report cards from Bistrack.",
        keywords: ["margin", "bistrack", "report card", "sales rep"],
      },
      {
        name: "Interior Door Form Summary",
        path: "/forms/IntDoorFormSummary",
        roles: ["Admin", "Executive Team"],
        description:
          "Every submitted interior door form, with its estimated total and PDF.",
      },
      {
        name: "Interior Door Admin",
        path: "/admin/doors/pricing",
        roles: ["Admin"],
        description:
          "What the interior door configurator sells and charges — slab styles, widths, labor, and cost overrides.",
        keywords: ["pricing", "slab", "configurator"],
      },
    ],
  },
  {
    id: "cabinets",
    title: "Cabinets",
    description: "Cabinet quotes and design tools",
    items: [
      {
        name: "Cabinet Quote Dashboard",
        path: "/cabinet-quotes",
        roles: ["Cabinet Team"],
        keywords: ["kitchen"],
      },
      {
        name: "Create Cabinet Quote",
        path: "/cabinet-quotes/new",
        roles: ["Cabinet Team"],
      },
      {
        name: "AI Render Enhancer",
        path: "/cabinet-quotes/design-render",
        roles: ["Cabinet Team"],
        keywords: ["photorealistic", "render", "design"],
      },
      {
        name: "Cabinet Admin",
        path: "/cabinet-quotes/admin",
        roles: ["Admin", "IT"],
        description:
          "Cabinet quote templates, checklist defaults, and collaborators.",
        keywords: ["templates", "collaborators"],
      },
    ],
  },
  {
    id: "sbcf",
    title: "SBCF",
    description: "Trusses, panels, and spec sheets",
    items: [
      {
        name: "SBCF Dashboard",
        path: "/sbcf",
        roles: ["*"],
        keywords: ["truss", "panel", "sales schedule"],
      },
      {
        name: "SBCF Admin",
        path: "/sbcfadmin",
        roles: ["Admin", "SBCF Admin", "SBCF Assigner", "SBCF Team"],
        keywords: ["spec sheet", "queue"],
      },
      {
        name: "Truss and Panel Documents",
        path: "/sbcf/documents",
        roles: ["*"],
        keywords: ["common stock", "drawings"],
      },
    ],
  },
  {
    id: "fleet",
    title: "Fleet",
    description: "Forklifts, trucks, and inspections",
    items: [
      {
        name: "Fleet Overview",
        path: "/fleet",
        roles: ["Admin", "Fleet Manager"],
        keywords: ["fleet", "dashboard", "today", "flagged"],
      },
      {
        name: "Forklifts",
        path: "/fleet/forklifts",
        roles: ["Admin", "Fleet Manager"],
        keywords: [
          "forklift",
          "forklift manager",
          "moffett",
          "unit",
          "add forklift",
          "out of service",
          "check-in",
          "checklist",
        ],
      },
      {
        name: "Vehicles",
        path: "/fleet/vehicles",
        roles: ["Admin", "Fleet Manager"],
        keywords: [
          "truck",
          "vehicle",
          "vehicle manager",
          "gps",
          "map",
          "where is",
          "driver",
          "vin",
          "odometer",
          "mileage",
        ],
      },
      {
        name: "Inspection Log",
        path: "/fleet/inspections",
        roles: ["Admin", "Fleet Manager"],
        keywords: [
          "inspection",
          "pre-trip",
          "checklist",
          "check-in",
          "history",
          "flagged",
          "not safe",
          "export",
        ],
      },
      {
        name: "Location Manager",
        path: "/fleet/forks/locationmanager",
        roles: ["Admin", "Fleet Manager"],
        keywords: ["branch", "location", "contacts", "manager", "shift", "supervisor", "kiosk"],
      },
    ],
  },
  {
    id: "safety",
    title: "Safety",
    description: "Toolbox talks and safety management",
    items: [
      {
        name: "Safety Overview",
        path: "/safety/overview",
        roles: ["Admin", "Safety Manager"],
        keywords: [
          "safety manager",
          "dashboard",
          "branch",
          "scorecard",
        ],
      },
      {
        name: "Safety Concerns",
        path: "/safety/concerns",
        roles: ["Admin", "Safety Manager"],
        keywords: [
          "safety concern",
          "safety submission",
          "hazard",
          "report",
          "unsafe",
          "photo",
        ],
      },
      {
        name: "Fire Extinguishers",
        path: "/safety/extinguishers",
        roles: ["Admin", "Safety Manager"],
        keywords: [
          "extinguisher",
          "fire",
          "monthly inspection",
          "inspection due",
          "overdue",
        ],
      },
      {
        name: "Training Videos",
        path: "/safety/training",
        roles: ["Admin", "Safety Manager"],
        keywords: [
          "training",
          "assign",
          "assigned training",
          "due date",
          "overdue",
          "upload video",
          "quiz",
        ],
      },
      {
        name: "Toolbox Talk Results",
        path: "/safety/toolbox-results",
        roles: ["Admin", "Safety Manager"],
        keywords: [
          "toolbox",
          "completion",
          "who watched",
          "quiz",
          "upload toolbox talk",
          "attendance",
        ],
      },
      {
        name: "Toolbox Talks",
        path: "/safety/toolbox-talks",
        roles: ["*"],
        keywords: [
          "safety",
          "video",
          "training",
          "talk",
          "toolbox",
          "monthly",
        ],
      },
    ],
  },
  {
    id: "everyday",
    title: "Everyday Tools",
    description: "Messages, weather, files, and help",
    // The everyday forms moved to the Forms hub (which is the defaultOpen
    // section now); their entries below stay navHidden so favorites and
    // widgets keyed to those paths keep working.
    items: [
      {
        name: "Messages",
        path: "/messages",
        roles: ["*"],
        keywords: [
          "chat",
          "dm",
          "direct message",
          "branch chat",
          "company chat",
          "group chat",
          "talk",
        ],
      },
      {
        name: "Caught in the Act",
        path: "/forms/recognition",
        roles: ["*"],
        keywords: ["recognition", "recognize", "shout out", "kudos"],
        navHidden: true, // reached via the Forms hub
      },
      {
        name: "Caught in the Act Entries",
        path: "/forms/recognition/entries",
        roles: ["Manager", "Admin"],
        keywords: ["recognition", "view entries"],
      },
      {
        name: "Order Problem",
        path: "/forms/order-problem",
        roles: ["*"],
        keywords: ["report", "wrong", "missing", "damaged"],
        navHidden: true, // reached via the Forms hub
      },
      {
        name: "Order Problems Dashboard",
        path: "/order-problems",
        roles: ["Admin", "Executive Team"],
        description:
          "Review, discuss, and resolve order problems reported by drivers and staff.",
      },
      {
        name: "Weather",
        path: "/dashboard/weather",
        roles: ["*"],
        keywords: ["forecast", "branch"],
      },
      {
        name: "SharePoint",
        path: "/dashboard/sharepoint",
        roles: ["*"],
        keywords: ["files", "file share", "documents"],
      },
      {
        name: "Request a Feature",
        path: "/forms/feature-request",
        roles: ["*"],
        keywords: ["idea", "suggestion", "broken", "change"],
        navHidden: true, // reached via the Forms hub
      },
      {
        name: "Help & FAQ",
        path: "/help",
        roles: ["*"],
        keywords: ["how do i", "support", "question"],
      },
    ],
  },
  {
    id: "admin",
    title: "Admin",
    description: "Users, apps, signage, and shop clocks",
    // One drawer row that opens the Admin Center, which lists every page
    // below as a card — see `hubPath` and "Organizing" in the header comment
    // before adding a page here.
    hubPath: "/admin",
    groups: [
      {
        title: null,
        items: [
          {
            // The route is open to everyone and the page only shows what
            // you can open; this is its audience — every role that can open
            // at least one page below.
            name: "Admin Center",
            path: "/admin",
            roles: [
              "Admin",
              "IT",
              "Executive Team",
              "General Manager",
              "Web Admin",
              "PCW Admin",
              "Mill Admin",
            ],
            keywords: ["admin", "settings", "manage", "tools", "console"],
          },
        ],
      },
      {
        title: "People & Access",
        items: [
          {
            name: "User Manager",
            path: "/admin/user/userManager",
            roles: ["Admin"],
            description:
              "Roles, branches, and account status for everyone on Node.",
            keywords: ["roles", "accounts", "users", "branch", "deactivate"],
          },
          {
            name: "Page Access",
            path: "/admin/access",
            roles: ["Admin"],
            description:
              "Let extra roles or people open a page, and create custom roles.",
            keywords: ["permissions", "roles", "custom roles", "grant", "access"],
          },
        ],
      },
      {
        title: "Node Apps",
        items: [
          {
            name: "Feature Requests",
            path: "/admin/feature-requests",
            roles: ["Admin", "IT"],
            description:
              "Triage and track feature and change requests from staff.",
            keywords: ["triage"],
          },
          {
            name: "Monthly Feedback",
            path: "/admin/feedback",
            roles: ["Admin", "IT"],
            description:
              "What people send from the monthly feedback popup, who dismissed it, and when each person sees it next.",
            keywords: [
              "feedback",
              "survey",
              "check-in",
              "popup",
              "suggestions",
              "dismissed",
              "responses",
            ],
          },
          {
            name: "Mobile App Codes",
            path: "/admin/app-codes",
            roles: ["Admin", "IT"],
            description:
              "Text or email Node Mobile download codes, and import new ones from Apple Business Manager.",
            keywords: [
              "node mobile",
              "download code",
              "redemption code",
              "redeem",
              "install app",
              "iphone",
              "app store",
              "text",
              "sms",
            ],
          },
          {
            name: "Assistant Usage",
            path: "/admin/assistant-usage",
            roles: ["Admin"],
            description:
              "How people use the Node Mobile assistant and Siri, and what they ask for that it can't do yet.",
            keywords: [
              "siri",
              "voice assistant",
              "ask node",
              "node mobile",
              "feature requests",
              "usage",
              "cost",
              "openai",
            ],
          },
        ],
      },
      {
        title: "Communications",
        items: [
          {
            name: "Signature Manager",
            path: "/admin/signatures",
            roles: ["Admin", "IT"],
            description:
              "Company email signatures and which groups get each one.",
            keywords: ["email", "outlook"],
          },
          {
            name: "Digital Signage",
            path: "/signage",
            roles: ["Admin", "Executive Team", "General Manager"],
            description:
              "The Raspberry Pi TVs at each branch — displays, media, and playlists.",
            keywords: [
              "tv",
              "screen",
              "display",
              "slideshow",
              "media loop",
              "raspberry pi",
              "advertising",
            ],
          },
          {
            name: "Big C Web Editor",
            path: "/admin/webeditor",
            roles: ["Web Admin", "Admin"],
            description:
              "Homepage highlights, blog stories, reviews, and location hours on the public website.",
            keywords: ["website", "trend"],
          },
        ],
      },
      {
        title: "Shop Floor",
        items: [
          {
            name: "PCW Hours",
            path: "/pcw",
            roles: ["Admin", "PCW Admin"],
            description:
              "PCW shop punch clock — hours by work order and worker, job schedule, and due dates.",
            keywords: ["punch", "time clock", "work order hours", "shop", "schedule", "deadline"],
          },
          {
            name: "Mill Shop Hours",
            path: "/mill-shop",
            roles: ["Admin", "Mill Admin"],
            description:
              "Mill - Fort Wayne punch clock — hours by work order and worker, job schedule, and due dates.",
            keywords: [
              "punch",
              "time clock",
              "work order hours",
              "mill",
              "fort wayne",
              "shop",
              "schedule",
              "deadline",
            ],
          },
        ],
      },
    ],
  },
];

// Normalizes a section to its groups: flat sections become one untitled group.
export const sectionGroups = (section) =>
  section.groups || [{ title: null, items: section.items || [] }];

// `path` + `granted` add Page Access grants (utils/routeAccess.js) on top of
// the item's roles.
export const checkRoles = (needed, mine, path, granted) => {
  if (!needed || needed[0] === "*") return true;
  return (
    needed.some((role) => mine.includes(role)) || isPathGranted(path, granted)
  );
};

// Every whitespace-separated token must appear somewhere in the item's
// searchable text. Shared by the drawer search and the header's quick-jump
// palette (CommandPalette).
export const matchesQuery = (haystack, query) =>
  query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token));

// Flat, role-filtered list of every page the user can open, with the text
// the search boxes match against.
export const searchableNavPages = (userRoles, grantedPaths) =>
  NAV_SECTIONS.flatMap((section) =>
    sectionGroups(section).flatMap((group) =>
      group.items
        .filter(
          (item) =>
            !item.disabled &&
            checkRoles(item.roles, userRoles, item.path, grantedPaths)
        )
        .map((item) => ({
          name: item.name,
          path: item.path,
          section: section.title,
          group: group.title,
          haystack: [
            item.name,
            ...(item.keywords || []),
            section.title,
            group.title || "",
          ]
            .join(" ")
            .toLowerCase(),
        }))
    )
  );
